import clsx from 'clsx';
import { Party } from 'api/file/interfaces/get-file-order-insights';
import HourglassTopOutlined from 'ui-kit/icons/HourglassTopOutlined';
import { theme } from 'ui-kit/theme/theme';
import { TransactionStatusEnum } from 'utilities/verification/types';
import ContactInfoDisplay from './ContactInfoDisplay';
import OrderInsightAlert from './OrderInsightAlerts';
import useStyles from './PdfListStyles';
import VerificationResults from './VerificationResults';

interface IEnabledPdfProducts {
  type: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  timezone?: string;
}

const EnabledPdfProducts = ({ type = 'ContactValidation', party, timezone }: IEnabledPdfProducts) => {
  const classes = useStyles();

  const isContactValidation = type === 'ContactValidation';

  const hideEditParty = {
    ContactValidation:
      !!party.phoneNumber ||
      !!party.emailAddress ||
      !!party?.contactValidation?.submittedData?.phoneNumber ||
      !!party?.contactValidation?.submittedData?.emailAddress,
    EnhancedIdentityVerification:
      !!party.phoneNumber ||
      !!party.emailAddress ||
      !!party?.enhancedIdentifyVerification?.submittedData?.phoneNumber ||
      !!party?.enhancedIdentifyVerification?.submittedData?.emailAddress,
  };

  if (hideEditParty[type]) {
    if (isContactValidation && party.contactValidation) {
      const contactInfo = party.contactValidation;
      return <ContactInfoDisplay {...contactInfo} isPdf />;
    }

    const identityResults = party.enhancedIdentifyVerification;
    if (identityResults && !isContactValidation) {
      switch (identityResults.transactionStatus) {
        case TransactionStatusEnum.Completed:
          return <VerificationResults {...identityResults} isPdf timezone={timezone} />;

        case TransactionStatusEnum.AwaitingUserAction:
        case TransactionStatusEnum.Pending:
          return (
            <OrderInsightAlert
              isPdf
              className={clsx(classes.removeFirstAlertMargin, classes.iconContainer)}
              alertWithContent={[
                {
                  content: 'Pending Response from Party',
                  severity: 'warning',
                  icon: <HourglassTopOutlined fill={theme.palette.status.pending} isPdf />,
                },
              ]}
            />
          );

        case TransactionStatusEnum.Failed:
          return (
            <OrderInsightAlert
              isPdf
              className={classes.removeFirstAlertMargin}
              unknowns={[
                {
                  severityName: 'Error',
                  category: 0,
                  categoryName: '',
                  type: 0,
                  typeName: '',
                  severity: 0,
                  title: '',
                  description: 'Party failed to complete identity verification',
                },
              ]}
            />
          );

        default:
          // Handle unknown status or fall through to default behavior
          break;
      }
    }
  }

  return (
    <OrderInsightAlert
      isPdf
      className={classes.removeFirstAlertMargin}
      unknowns={[
        {
          severityName: 'None',
          category: 0,
          categoryName: '',
          type: 0,
          typeName: '',
          severity: 0,
          title: '',
          description: 'Not requested for the party',
        },
      ]}
    />
  );
};

export default EnabledPdfProducts;
